<template>
  <v-card flat>
    <!-- Datatable filter -->
    <v-card-title v-if="showGridFilter">
      <v-layout row wrap>
        <v-flex sm6>
          <v-text-field append-icon="filter_list" :label="messages.label.PROVIDER_GROUP_FILTER" single-line clearable
            hide-details @input="value => filterProviderGroupsList(value)" v-model="filterInput">
          </v-text-field>
        </v-flex>
      </v-layout>
    </v-card-title>
    <!-- Data Table -->
    <v-card-text>
      <v-data-table :headers="headers" :items="filteredSearchResult" :pagination.sync="pagination"
        :total-items="totalItems" :loading="providerGroupsLoading" :rows-per-page-items="rowsPerPagesArray">
        <!-- Items -->
        <template slot="items" slot-scope="props">
          <tr @click="showCurrentProviderGroupDetails(props.item)"
            :active="!!selectedPG && selectedPG.id == props.item.id" class="pointer">
            <td class="text-xs-left">{{ props.item.title }}</td>
            <td class="text-xs-left">{{ props.item._updated }}</td>
            <td class="text-xs-left">{{ props.item._added }}</td>
          </tr>
        </template>
        <!-- No Data -->
        <template slot="no-data">
          <v-alert :value="true" color="info" class="black--text" icon="info">
            {{ messages.info.NO_PROVIDER_GROUPS }}
          </v-alert>
        </template>
        <!-- No Results -->
        <template slot="no-results">
          <v-alert :value="true" color="info" class="black--text" icon="info">
            {{ messages.info.NO_FILTER_PROVIDER_GROUPS }}
          </v-alert>
        </template>
        <!-- Page Text -->
        <template slot="pageText" slot-scope="props">
          {{ props.pageStart }} - {{ props.pageStop }} of {{ props.itemsLength }}
        </template>
      </v-data-table>
    </v-card-text>
  </v-card>
</template>

<script>
import { mapState, mapGetters, mapMutations } from "vuex"
import ProvidersService from "@/Providers/ProvidersService"
import AppService from "@/AppService"
import * as providersDataService from "@/Providers/ProvidersDataService"
import * as authDataService from "@/Auth/AuthDataService"
import * as appDataService from "@/AppDataService"
import messages from "@/Providers/messages.json"
import { VOD_SYCN_PREINGEST_DATA_SERVICE_URL } from "@/store/constants/getter-types"
import { SET_MPX_SUB_ACCOUNTS } from "@/store/constants/mutation-types"

/**
 * initialData
 */
function initialData(instance) {
  return {
    filterInput: "",
    selectedPG: {},
    providerGroupsLoading: false,
    pagination: {
      sortBy: "title",
      page: 1,
      rowsPerPage: 25,
      descending: false
    },
    totalItems: 0,
    rowsPerPagesArray: [25, 50, 75, 100],
    headers: instance.getTableHeaders(),
    providerGroupsList: [],
    filteredSearchResult: []
  }
}

export default {
  name: "ProviderGroupsList",
  data: function () {
    return initialData(this)
  },
  computed: {
    ...mapState([
      "mpxParentAccount", "mpxSubAccounts"
    ]),
    ...mapGetters([
      VOD_SYCN_PREINGEST_DATA_SERVICE_URL
    ]),
    showGridFilter() {
      return this.providerGroupsList.length > 0
    }
  },
  watch: {
    /**
     * Watch handler for pagination object
     */
    pagination: {
      handler(newVal, oldVal) {
        if (oldVal !== newVal) {
          // NOTE: Initial load triggers a change on 'totalItems' property
          this.retrieveProviderGroups()
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapMutations([
      SET_MPX_SUB_ACCOUNTS
    ]),

    /**
     * reset
     */
    reset() {
      Object.assign(this.$data, initialData(this))
    },

    /**
     * getTableHeaders - onstruct array of objects to show table headers
     * @return {array} headers
     */
    getTableHeaders() {
      let headers = [
        {
          text: messages.label.TH_PROVIDER_GROUP,
          align: "left",
          value: "title"
        },
        {
          text: messages.label.TH_LAST_UPDATED,
          align: "left",
          value: "updated"
        },
        {
          text: messages.label.TH_ADDED_DATE,
          align: "left",
          value: "added"
        }
      ]
      return headers
    },

    /**
     * filterProviderGroupsList - Filter search result with text value
     * NOTE: This filters available search results on the client
     * @param {string} search
     */
    filterProviderGroupsList(search) {
      if (search) {
        search = search.toString().toLowerCase()
        const displayedKeys = [
          "title",
          "_updated",
          "_added"
        ]
        this.filteredSearchResult = this.providerGroupsList.filter((obj) => {
          let filteredObj = this.$lodash.pick(obj, displayedKeys)
          return Object.keys(filteredObj).some(function (key) {
            if (filteredObj[key] !== null) {
              return filteredObj[key]
                .toString()
                .toLowerCase()
                .includes(search)
            }
          })
        })
      } else {
        this.filteredSearchResult = this.providerGroupsList
      }
    },

    /**
     * Get pagination params
     * @param {object} pagination
     * @return {string} range string example: 0-25
     */
    getPageParams(pagination) {
      let startRange
      let endRange
      if (pagination.page === 1) {
        startRange = 1
        endRange = pagination.rowsPerPage
      } else {
        startRange = (pagination.page - 1) * pagination.rowsPerPage + 1
        endRange = startRange + pagination.rowsPerPage - 1
      }
      return startRange + "-" + endRange
    },

    /**
     * getSortParams - Construct sorting param
     * @param  {object} pagination pagination object
     * @return {string}            ex: '<filedName>|desc'
     */
    getSortParams(pagination) {
      let sortParams = ""
      sortParams = this.$lodash.isEmpty(pagination.sortBy) ? "title" : pagination.sortBy
      if (pagination.descending) {
        sortParams += "|desc"
      }
      return sortParams
    },

    /**
     * retrieveProviderGroups - Retrieve all providers by making API call
     * @param {boolean} updateSubAccounts
     * @returns {Promise}
     */
    retrieveProviderGroups(updateSubAccounts) {
      let subAccountsPromise = {}
      this.providerGroupsLoading = true
      this.providerGroupsList = []
      if (updateSubAccounts) {
        subAccountsPromise = authDataService.mpxSubAccounts(this.mpxParentAccount.id)
          .then((response) => {
            let subAccounts = response.data.entries
            this.SET_MPX_SUB_ACCOUNTS(subAccounts)
            return appDataService.setMPXSubAccounts(subAccounts)
          })
      } else {
        subAccountsPromise = new Promise(function (resolve) {
          resolve()
        })
      }

      return subAccountsPromise.then(() => {
        let providerGroupsQueryObject = {
          range: this.getPageParams(this.pagination),
          sort: this.getSortParams(this.pagination),
          count: true,
          byOwnerId: AppService.getSubAccountsIdsString(this.mpxSubAccounts)
        }
        return providersDataService
          .retrieveProviderGroups(providerGroupsQueryObject, this.VOD_SYCN_PREINGEST_DATA_SERVICE_URL)
          .then((response) => {
            if (!this.$lodash.isUndefined(response.data.entries)) {
              this.providerGroupsList = response.data.entries
              this.providerGroupsList = ProvidersService.addClientOnlyFields(this.providerGroupsList)
              this.filterProviderGroupsList(this.filterInput)
              this.totalItems = response.data.totalResults
            } else {
              this.providerGroupsList = []
            }
            return this.providerGroupsList
          })
          .catch((error) => {
            this.providerGroupsList = []
            this.filteredSearchResult = []
            this.totalItems = 0
            return Promise.reject(error)
          })
          .finally(() => {
            this.providerGroupsLoading = false
          })
      })
    },

    /**
     * refreshProviderGroupsList - Update provider groups list
     * @param {string} reason
     * @param {string} providerGroupId
     */
    refreshProviderGroupsList(reason, providerGroupId) {
      if (reason === "add") {
        // FIXME: changing pagination object should trigger
        // 'watch' but doesn't seem to detect changes propertly
        // calling 'retrieveProviders' for now
        this.pagination.sortBy = "added"
        this.pagination.descending = true
      } else if (reason === "update") {
        this.pagination.sortBy = "updated"
        this.pagination.descending = true
      } else if (reason === "delete") {
        this.updateSelectedRow()
        this.pagination.sortBy = "title"
        this.pagination.descending = false
      }
      let updateSubAccounts = (reason === "add")
      // Re-retrieve provider groups and display updated data on the details sheet
      this.retrieveProviderGroups(updateSubAccounts)
        .then(() => {
          if (providerGroupId) {
            this.selectedPG = this.$lodash.find(this.filteredSearchResult, (item) => {
              return item.id === providerGroupId
            })
            if (!this.$lodash.isUndefined(this.selectedPG)) {
              ProvidersService.$emit("event:showProviderGroupDetailsSheet", this.selectedPG)
            }
          }
        })
    },

    /**
     * showCurrentProviderGroupDetails - Show selected provider gorup details
     * @param {object} providerGroup
     */
    showCurrentProviderGroupDetails(providerGroup) {
      this.updateSelectedRow(providerGroup)
      ProvidersService.$emit(
        "event:showProviderGroupDetailsSheet",
        providerGroup
      )
    },

    /**
     * updateSelectedRow - Update selected Provider Group obj when the user clicks to create a new provider group
     * @param {object} providerGroup
     */
    updateSelectedRow(providerGroup) {
      if (this.$lodash.isUndefined(providerGroup)) {
        this.selectedPG = {}
      } else {
        this.selectedPG = providerGroup
      }
    }
  },
  created() {
    /**
     * Non reactive properties
     */
    this.messages = messages

    /**
     * Events
     */
    ProvidersService.$on(
      "event:refreshProviderGroupsList",
      this.refreshProviderGroupsList
    )
    ProvidersService.$on(
      "event:hideProviderGroupDetailsSheet",
      this.updateSelectedRow
    )
    AppService.$on("event:mpxAccountChanged", this.reset)
  },
  beforeDestroy() {
    ProvidersService.$off(
      "event:refreshProviderGroupsList",
      this.refreshProviderGroupsList
    )
    ProvidersService.$off(
      "event:hideProviderGroupDetailsSheet",
      this.updateSelectedRow
    )
    AppService.$off("event:mpxAccountChanged", this.reset)
  }
}
</script>

<style scoped>
</style>



// WEBPACK FOOTER //
// src/Providers/ProviderGroups/ProviderGroupsList.vue